import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Form, Row, Col, Table, Badge } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FaClock, FaCalendarAlt, FaGraduationCap } from 'react-icons/fa';
import { useSelector, useDispatch } from 'react-redux';
import { getUserDetails } from '../../../actions/userActions';

function WorkTime() {
  const [birthDate, setBirthDate] = useState('');
  const [schoolStartYear, setSchoolStartYear] = useState('');
  const [result, setResult] = useState(null);

  const dispatch = useDispatch();
  
  const userDetails = useSelector(state => state.userDetails);
  const { user } = userDetails;

  const userLogin = useSelector(state => state.userLogin);
  const { userInfo } = userLogin;

  // Automatyczne pobranie danych użytkownika przy załadowaniu komponentu
  useEffect(() => {
    if (userInfo && (!user || !user.name)) {
      dispatch(getUserDetails('profile'));
    }
  }, [dispatch, userInfo, user]);

  // Automatyczne uzupełnienie daty urodzenia z profilu użytkownika
  useEffect(() => {
    if (user && user.birth_date && !birthDate) {
      const formattedDate = user.birth_date.substring(0, 10);
      setBirthDate(formattedDate);
    }
  }, [user, birthDate]);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  const calculateWorkTime = useCallback(() => {
    if (!birthDate || !schoolStartYear) return;

    const birth = new Date(birthDate);
    const schoolStart = new Date(schoolStartYear, 8, 1);

    let ageAtStart = schoolStart.getFullYear() - birth.getFullYear();
    const m1 = 8; // wrzesień
    const m2 = birth.getMonth();
    if (m1 < m2 || (m1 === m2 && schoolStart.getDate() < birth.getDate())) {
      ageAtStart--;
    }

    const turns16 = new Date(birth.getFullYear() + 16, birth.getMonth(), birth.getDate());

    const classes = [];
    for (let classNum = 1; classNum <= 3; classNum++) {
      const classStartYear = parseInt(schoolStartYear) + (classNum - 1);
      const classStart = new Date(classStartYear, 8, 1);
      const classEnd = new Date(classStartYear + 1, 7, 31);

      let workTime;
      let note = '';

      if (turns16 > classStart && turns16 <= classEnd) {
        workTime = '6h / 8h';
        note = `Do ${turns16.toLocaleDateString('pl-PL')} - 6h, potem 8h`;
      } else if (turns16 <= classStart) {
        workTime = '8h';
        note = 'Ukończone 16 lat';
      } else {
        workTime = '6h';
        note = 'Poniżej 16 lat';
      }

      classes.push({
        number: classNum,
        year: `${classStartYear}/${classStartYear + 1}`,
        workTime,
        note
      });
    }

    setResult({
      ageAtStart,
      turns16: turns16.toLocaleDateString('pl-PL'),
      classes
    });
  }, [birthDate, schoolStartYear]);

  useEffect(() => {
    if (birthDate && schoolStartYear) {
      calculateWorkTime();
    } else {
      setResult(null);
    }
  }, [birthDate, schoolStartYear, calculateWorkTime]);

  const handleDateChange = (e) => setBirthDate(e.target.value);
  const handleSchoolYearChange = (e) => setSchoolStartYear(e.target.value);

  // Dane dla tabel poglądowych
  const workTimeData = [
    {
      startAge: 14,
      class1: { hours: 'nie więcej niż 6h', note: 'Poniżej 16 lat' },
      class2: { hours: 'nie więcej niż 6h/8h*', note: '*w zależności kiedy młodociany kończy 16 lat' },
      class3: { hours: 'nie więcej niż 8h', note: 'Ukończone 16 lat' }
    },
    {
      startAge: 15,
      class1: { hours: 'nie więcej niż 6h', note: 'Poniżej 16 lat' },
      class2: { hours: 'nie więcej niż 6h/8h*', note: '*w zależności kiedy młodociany kończy 16 lat' },
      class3: { hours: 'nie więcej niż 8h', note: 'Ukończone 16 lat' }
    },
    {
      startAge: 16,
      class1: { hours: 'nie więcej niż 8h', note: 'Ukończone 16 lat' },
      class2: { hours: 'nie więcej niż 8h', note: 'Ukończone 16 lat' },
      class3: { hours: 'nie więcej niż 8h', note: 'Ukończone 16 lat' }
    },
    {
      startAge: 17,
      class1: { hours: 'nie więcej niż 8h', note: 'Ukończone 16 lat' },
      class2: { hours: 'nie więcej niż 8h', note: 'Ukończone 16 lat' },
      class3: { hours: 'nie więcej niż 8h', note: 'Ukończone 16 lat' }
    }
  ];

  return (
    <div style={{ minHeight: '100vh', paddingTop: '2rem', paddingBottom: '4rem', width: '100%' }}>
      <Container>
        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-5"
        >
          <h1 className="text-white fw-bold mb-3">Czas pracy młodocianych</h1>
          <p className="text-white-50" style={{ fontSize: '1.2rem' }}>
            Sprawdź ile godzin możesz pracować w poszczególnych klasach
          </p>
        </motion.div>

        <Row className="justify-content-center">
          <Col xs={12} lg={10}>
            {/* KALKULATOR */}
            <motion.div {...fadeInUp} className="mb-5">
              <Card className="border-0 shadow-lg" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                <Card.Header
                  style={{
                    background: 'linear-gradient(135deg, var(--bs-primary) 0%, #0056b3 100%)',
                    border: 'none',
                    padding: '1.5rem'
                  }}
                >
                  <h4 className="text-white mb-0 fw-bold">
                    Kalkulator czasu pracy
                  </h4>
                </Card.Header>
                <Card.Body className="p-4">
                  <Row className="g-4">
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label className="fw-medium d-flex align-items-center">
                          <FaCalendarAlt className="me-2 text-primary" />
                          Data urodzenia
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={birthDate}
                          onChange={handleDateChange}
                          style={{
                            borderRadius: '10px',
                            border: '2px solid #e9ecef',
                            padding: '0.75rem'
                          }}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label className="fw-medium d-flex align-items-center">
                          <FaGraduationCap className="me-2 text-primary" />
                          Rok rozpoczęcia nauki zawodu
                        </Form.Label>
                        <Form.Control
                          type="number"
                          placeholder="np. 2024"
                          value={schoolStartYear}
                          onChange={handleSchoolYearChange}
                          min="2020"
                          max="2030"
                          style={{
                            borderRadius: '10px',
                            border: '2px solid #e9ecef',
                            padding: '0.75rem'
                          }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* WYNIK */}
                  {result && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                      className="mt-4"
                    >
                      <div
                        className="p-4 mb-3"
                        style={{
                          background: 'linear-gradient(135deg, rgba(67, 233, 123, 0.1) 0%, rgba(56, 249, 215, 0.1) 100%)',
                          borderRadius: '15px',
                          border: '2px solid rgba(67, 233, 123, 0.3)'
                        }}
                      >
                        <h5 className="fw-bold mb-3">
                          <FaClock className="me-2 text-success" />
                          Twój harmonogram czasu pracy
                        </h5>
                        <Row className="g-3 mb-3 align-items-stretch">
                          <Col xs={12} sm={6} className="d-flex">
                            <div
                              className="text-center p-3 flex-fill h-100 d-flex flex-column justify-content-center"
                              style={{
                                background: 'rgba(255,255,255,0.5)',
                                borderRadius: '10px',
                                minHeight: '120px'
                              }}
                            >
                              <small className="text-muted d-block mb-1">Wiek rozpoczęcia nauki zawodu</small>
                              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#667eea' }}>
                                {result.ageAtStart} lat
                              </div>
                            </div>
                          </Col>
                          <Col xs={12} sm={6} className="d-flex">
                            <div
                              className="text-center p-3 flex-fill h-100 d-flex flex-column justify-content-center"
                              style={{
                                background: 'rgba(255,255,255,0.5)',
                                borderRadius: '10px',
                                minHeight: '120px'
                              }}
                            >
                              <small className="text-muted d-block mb-1">Data ukończenia 16 lat</small>
                              <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: '#f093fb' }}>
                                {result.turns16}
                              </div>
                            </div>
                          </Col>
                        </Row>
                        <div className="table-responsive">
                          <Table className="mb-0">
                            <thead style={{ background: 'rgba(255,255,255,0.3)' }}>
                              <tr>
                                <th className="fw-bold">Klasa</th>
                                <th className="fw-bold">Rok szkolny</th>
                                <th className="fw-bold text-center">Czas pracy</th>
                                <th className="fw-bold">Uwagi</th>
                              </tr>
                            </thead>
                            <tbody>
                              {result.classes.map((cls) => (
                                <tr key={cls.number}>
                                  <td>
                                    <Badge bg="primary" style={{ fontSize: '0.9rem' }}>
                                      Klasa {cls.number}
                                    </Badge>
                                  </td>
                                  <td>{cls.year}</td>
                                  <td className="text-center">
                                    <strong
                                      style={{
                                        color: cls.workTime.includes('/') ? '#f093fb' : '#43e97b',
                                        fontSize: '1.1rem'
                                      }}
                                    >
                                      {cls.workTime}
                                    </strong>
                                  </td>
                                  <td>
                                    <small className="text-muted">{cls.note}</small>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </Card.Body>
              </Card>
            </motion.div>

            {/* PRZEPISY */}
            <motion.div {...fadeInUp} transition={{ delay: 0.2 }} className="mb-5">
              <Card
                className="border-0 shadow-lg"
                style={{
                  borderRadius: '20px',
                  overflow: 'hidden',
                  background: 'rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Card.Body className="p-4">
                  <h4 className="text-white fw-bold mb-3">Art. 202 Kodeksu Pracy</h4>
                  <div className="p-3" style={{ background: 'rgba(102, 126, 234, 0.1)', borderRadius: '10px' }}>
                    <h5 className="text-white mb-2">
                      <strong>§ 1.</strong> Czas pracy młodocianego w wieku <strong>do 16 lat</strong> nie może przekraczać <strong>6 godzin na dobę.</strong>
                    </h5>
                    <h5 className="text-white mb-0">
                      <strong>§ 2.</strong> Czas pracy młodocianego w wieku <strong>powyżej 16 lat</strong> nie może przekraczać <strong>8 godzin na dobę.</strong>
                    </h5>
                  </div>
                </Card.Body>
              </Card>
            </motion.div>

            {/* TABELE POGLĄDOWE */}
            <motion.div {...fadeInUp} transition={{ delay: 0.3 }}>
              <Card className="border-0 shadow-lg" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                <Card.Header
                  style={{
                    background: 'linear-gradient(135deg, #21D4FD 0%, #B721FF 100%)',
                    border: 'none',
                    padding: '1.5rem'
                  }}
                >
                  <h4 className="text-white mb-0 fw-bold">
                    Tabela poglądowa - czas pracy według wieku rozpoczęcia nauki zawodu
                  </h4>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table className="mb-0" striped>
                      <thead style={{ background: '#f8f9fa' }}>
                        <tr>
                          <th className="fw-bold p-3">Wiek rozpoczęcia nauki zawodu</th>
                          <th className="fw-bold p-3 text-center">Klasa I</th>
                          <th className="fw-bold p-3 text-center">Klasa II</th>
                          <th className="fw-bold p-3 text-center">Klasa III</th>
                        </tr>
                      </thead>
                      <tbody>
                        {workTimeData.map((row) => (
                          <tr key={row.startAge}>
                            <td className="p-3">
                              <Badge bg="primary" style={{ fontSize: '1rem', padding: '0.5rem 1rem' }}>
                                {row.startAge} lat
                              </Badge>
                            </td>
                            <td className="p-3">
                              <div className="text-center">
                                <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{row.class1.hours}</div>
                                <small className="text-muted">{row.class1.note}</small>
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="text-center">
                                <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{row.class2.hours}</div>
                                <small className="text-muted">{row.class2.note}</small>
                              </div>
                            </td>
                            <td className="p-3">
                              <div className="text-center">
                                <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>{row.class3.hours}</div>
                                <small className="text-muted">{row.class3.note}</small>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </motion.div>
            <motion.div
              {...fadeInUp}
              transition={{ delay: 0.6 }}
              className="mt-4"
            >
              <Card
                className="border-0 shadow-lg"
                style={{
                  borderRadius: '20px',
                  background: 'rgba(255, 0, 0, 0.1)', 
                  border: '2px solid rgba(255, 0, 0, 0.3)' 
                }}
              >
                <Card.Body className="p-4">
                  <h5 className="mb-0">
                    * Jeżeli młodociany powtarza klasę ucząc się w tym samym zawodzie znaczenie ma jego wiek a nie klasa do której chodzi.
                  </h5>
                </Card.Body>
              </Card>
            </motion.div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default WorkTime;